import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  CardTitle,
  CardText,
} from "reactstrap";
import {
  Typography,
  Avatar,
  Rating,
  Chip,
} from "@mui/material";
import constants from "constants";
import { useTranslation } from "context/TranslationProvider";
import helpers from "utils/helpers";

const UserReviewsModal = ({ isOpen, toggle, reviews = [] }) => {
  const { t } = useTranslation();
  const INITIAL_COUNT = 2;

  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

  useEffect(() => {
    if (isOpen) {
      setVisibleCount(INITIAL_COUNT);
    }
  }, [isOpen]);

  const visibleReviews = reviews.slice(0, visibleCount);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>{t("page.user-profile.sections.reviews", "Your Reviews")}</ModalHeader>

      <ModalBody className="border navbar-nav-scroll">
        {!reviews.length ? (
          <Typography>{t("page.user-profile.sections.noReviews", "No reviews yet.")}</Typography>
        ) : (
          <div className="d-flex flex-column gap-3">
            {visibleReviews.map((review) => {
              const profilePicUrl = review.reviewer_id?.profilePictureUrl
                ? `${constants.s3Bucket}/profile-pics/${review.reviewer_id.profilePictureUrl}`
                : "";

              const currentStatus = helpers.normalizeOrderStatus(
                review.order_id?.status
              );
              const statusClass = helpers.getStatusClass(
                currentStatus,
                "order"
              );

              return (
                <Card key={review._id} className="shadow-md rounded-lg my-2">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center gap-3">
                        <Avatar
                          src={profilePicUrl}
                          alt={review.reviewer_id?.firstName}
                          className="user-reviews-modal-avatar"
                        />

                        <div className="ml-2">
                          <CardTitle tag="h5" className="mb-0">
                            {review.reviewer_id?.firstName}{" "}
                            {review.reviewer_id?.lastName}
                          </CardTitle>

                          <small className="text-muted">
                            {new Date(review.createdAt).toLocaleDateString()}
                          </small>
                        </div>
                      </div>
                      <div>
                        {review.order_id && (
                          <Chip
                            label={review.order_id.orderid}
                            className="mr-2 font-weight-bold bg-transparent"
                          />
                        )}
                        {review.order_id && (
                          <Chip
                            label={t(`page.orders.status.${currentStatus}`, helpers.ORDER_STATUS_API_MAP[currentStatus] || currentStatus)}
                            size="small"
                            variant="outlined"
                            className={`user-reviews-modal__status-chip font-medium ${statusClass}`}
                          />
                        )}
                      </div>
                    </div>

                    <Rating
                      value={review.rating}
                      readOnly
                      size="small"
                      className="mb-2"
                    />

                    <CardText className="text-muted">
                      {review.message || "—"}
                    </CardText>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          {t("button.close", "Close")}
        </Button>

        {visibleCount < reviews.length && (
          <Button color="warning" onClick={() => setVisibleCount(reviews.length)}>
            {t("button.loadAll", "Load All")}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default UserReviewsModal;
